import React, { useLayoutEffect, useRef, useState } from 'react';
import IntlTelInput from '@intl-tel-input/react';
import { loadPhoneUtils } from '../utils/lazyModules.js';

/**
 * International phone field backed by intl-tel-input (flag + dial code selector,
 * country search, libphonenumber formatting/validation via the utils, which are
 * fetched on mount as a sibling file of the bundle — see utils/lazyModules.js).
 *
 * The country dropdown is detached (position: fixed) so it floats over the whole
 * page instead of being clipped by the acquisition form's `overflow: hidden`. It must be
 * portaled to an element **inside the same shadow root as the input**: iti links
 * the dropdown to the input with CSS anchor positioning, which can't cross the
 * shadow boundary — portaling to document.body would leave it unanchored. We
 * append a dedicated host div directly to the shadow root (a sibling of the
 * React mount point, so React never reconciles it away) and hand that to iti as
 * `dropdownParent`. Its z-index is lifted above the modal overlay in iti-overrides.css.
 *
 * The placeholder is intentionally left to intl-tel-input (POLITE policy) so it
 * shows a country-matched example formatted exactly how iti formats typed input.
 *
 * @param {{
 *   country: string,
 *   onCountryChange: (iso2:string) => void,
 *   value: string,                 // E.164, controlled
 *   onChange: (e164:string) => void,
 *   error?: boolean
 * }} props
 */
export function PhoneField({ country, onCountryChange, value, onChange, error }) {
  const anchorRef = useRef(null);
  const [dropdownParent, setDropdownParent] = useState(null);
  const [ready, setReady] = useState(false);

  // Resolve the dropdown host once the field is in the DOM and we can read its
  // root node. iti needs `dropdownParent` at init, so we gate the input render
  // until it's known (a layout effect resolves it before paint — no flash).
  useLayoutEffect(() => {
    const root = anchorRef.current?.getRootNode?.();
    if (root && typeof ShadowRoot !== 'undefined' && root instanceof ShadowRoot) {
      let host = root.querySelector(':scope > .tdw-iti-dropdown-host');
      if (!host) {
        host = document.createElement('div');
        host.className = 'tdw-iti-dropdown-host';
        root.appendChild(host);
      }
      setDropdownParent(host);
    } else {
      setDropdownParent(typeof document !== 'undefined' ? document.body : null);
    }
    setReady(true);
  }, []);

  return (
    <div ref={anchorRef}>
      {ready && (
        <IntlTelInput
          initialCountry={(country || 'br').toLowerCase()}
          value={value || ''}
          onChangeNumber={(num) => onChange?.(num)}
          onChangeCountry={(iso2) => onCountryChange?.(iso2 ? iso2.toUpperCase() : '')}
          loadUtils={loadPhoneUtils}
          separateDialCode
          countrySearch
          placeholderNumberType="MOBILE"
          dropdownParent={dropdownParent || undefined}
          inputProps={{ className: `tdw-input${error ? ' error' : ''}`, inputMode: 'tel' }}
        />
      )}
    </div>
  );
}
